import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface OccurrenceLike {
    event: { id: string };
}

interface UseDirectoryItemOccurrenceSelectionOptions<T extends OccurrenceLike> {
    occurrences: T[] | undefined;
    selectedOccurrence: T | null | undefined;
    setSelectedOccurrence: (occurrence: T) => void;
    isLoading: boolean;
    showEvents: () => void;
    hasMore?: boolean;
    loadMore?: () => void;
}

interface UseDirectoryItemOccurrenceSelectionResult<T extends OccurrenceLike> {
    handleOccurrenceSelect: (occurrence: T) => void;
}

export function useDirectoryItemOccurrenceSelection<T extends OccurrenceLike>({
    occurrences,
    selectedOccurrence,
    setSelectedOccurrence,
    isLoading,
    showEvents,
    hasMore,
    loadMore,
}: UseDirectoryItemOccurrenceSelectionOptions<T>): UseDirectoryItemOccurrenceSelectionResult<T> {
    const [searchParams] = useSearchParams();
    const watchEventId = searchParams.get('watch');

    useEffect(() => {
        if (isLoading || !occurrences || occurrences.length === 0) return;

        if (watchEventId) {
            const watchTarget = occurrences.find(
                (occurrence) => occurrence.event.id === watchEventId,
            );
            if (watchTarget) {
                if (watchTarget.event.id !== selectedOccurrence?.event.id) {
                    setSelectedOccurrence(watchTarget);
                    showEvents();
                }
                return;
            }

            if (hasMore) {
                loadMore?.();
                return;
            }
        }

        if (!selectedOccurrence) {
            setSelectedOccurrence(occurrences[0]);
        }
    }, [
        isLoading,
        occurrences,
        selectedOccurrence,
        setSelectedOccurrence,
        watchEventId,
        showEvents,
        hasMore,
        loadMore,
    ]);

    const handleOccurrenceSelect = (occurrence: T) => {
        setSelectedOccurrence(occurrence);
        showEvents();
    };

    return { handleOccurrenceSelect };
}
